import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTransactionRecords, TransactionRecord } from '@/hooks/useAccount'
import { 
  Receipt, 
  Download,
  RefreshCw,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Filter
} from 'lucide-react'
import { format } from 'date-fns'

export default function Transactions() {
  const [page, setPage] = useState(0)
  const [searchFilters, setSearchFilters] = useState({
    startDate: '',
    endDate: '',
  })

  const { data: transactionData, isLoading, refetch } = useTransactionRecords({
    page,
    size: 10,
    ...searchFilters
  })



  // 处理搜索
  const handleSearch = () => {
    setPage(0) // 重置到第一页
  }

  // 处理重置
  const handleReset = () => {
    setSearchFilters({
      startDate: '',
      endDate: '',
    })
    setPage(0)
  }

  const handleExport = async () => {
    try {
      // 调用导出API
      const response = await fetch('/api/v1/tenant/billing/records/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          startDate: searchFilters.startDate || undefined,
          endDate: searchFilters.endDate || undefined,
        }),
      })
      
      if (!response.ok) {
        throw new Error('导出失败')
      }
      
      const result = await response.json()
      
      if (result.status === 'READY') {
        // 下载文件
        const downloadUrl = `/api/v1/tenant/billing/records/export/${result.exportId}/download`
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `费用明细_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('导出失败:', error)
      alert('导出失败，请稍后重试')
    }
  }

  const transactionColumns: ColumnDef<TransactionRecord>[] = [
    {
      accessorKey: 'transactionNo',
      header: '交易单号',
      cell: ({ row }) => {
        return <span className="font-mono text-sm">{row.getValue('transactionNo')}</span>
      },
    },
    {
      accessorKey: 'transactionType',
      header: '交易类型',
      cell: ({ row }) => {
        const type = row.getValue('transactionType') as string
        const typeMap = {
          RECHARGE: { label: '充值', variant: 'success' as const, icon: ArrowUpRight },
          CONSUMPTION: { label: '消费', variant: 'destructive' as const, icon: ArrowDownRight },
          REFUND: { label: '退款', variant: 'default' as const, icon: ArrowUpRight },
          FREEZE: { label: '冻结', variant: 'secondary' as const, icon: Activity },
          UNFREEZE: { label: '解冻', variant: 'secondary' as const, icon: Activity },
        }
        const typeInfo = typeMap[type as keyof typeof typeMap] || { label: type, variant: 'secondary' as const, icon: Activity }
        const Icon = typeInfo.icon
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: '金额',
      cell: ({ row }) => {
        const amount = row.getValue('amount') as number
        const type = row.getValue('transactionType') as string
        const isPositive = type === 'RECHARGE' || type === 'REFUND' || type === 'UNFREEZE'
        return (
          <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : '-'}¥{amount.toFixed(2)}
          </span>
        )
      },
    },
    {
      accessorKey: 'balanceAfter',
      header: '余额',
      cell: ({ row }) => {
        const balanceAfter = row.getValue('balanceAfter') as number
        return <span className="font-medium">¥{balanceAfter > 0 ? balanceAfter.toFixed(2) : '-'}</span>
      },
    },
    {
      accessorKey: 'description',
      header: '描述',
      cell: ({ row }) => {
        const description = row.getValue('description') as string
        return (
          <span className="max-w-[200px] truncate" title={description}>
            {description || '-'}
          </span>
        )
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: '支付方式',
      cell: ({ row }) => {
        const method = row.getValue('paymentMethod') as string
        const methodMap = {
          ALIPAY: '支付宝',
          WECHAT: '微信支付',
          BANK: '银行转账',
          MANUAL: '人工充值',
          SELF: '自助充值',
          SYSTEM: '系统调整',
        }
        return methodMap[method as keyof typeof methodMap] || '-'
      },
    },
    {
      accessorKey: 'createdAt',
      header: '交易时间',
      cell: ({ row }) => {
        return format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm:ss')
      },
    },
  ]

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">费用明细</h2>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            刷新
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            导出
          </Button>
        </div>
      </div>



      {/* 搜索筛选区域 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            查询条件
          </CardTitle>
          <CardDescription>设置查询条件来筛选交易记录</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>开始日期</Label>
              <Input
                type="date"
                value={searchFilters.startDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>结束日期</Label>
              <Input
                type="date"
                value={searchFilters.endDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={handleReset}>
              重置
            </Button>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Receipt className="h-5 w-5 mr-2" />
            交易记录
          </CardTitle>
          <CardDescription>
            查看账户所有交易明细记录
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={transactionColumns}
              data={transactionData?.records || []}
              pagination={{
                page: page + 1, // 转换为1-based
                totalPages: transactionData?.pages ?? 0,
                totalItems: transactionData?.total ?? 0,
                itemsPerPage: 10,
                onPageChange: (newPage) => setPage(newPage - 1), // 转换回0-based
                onItemsPerPageChange: () => {}, // 固定大小为10
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
